import React, { useCallback, useEffect, useId, useState } from 'react';
import Button from './Button';
import { ConfirmOptions, registerConfirm } from '../../utils/confirmAction';

type Pending = ConfirmOptions & { resolve: (value: boolean) => void };

const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<Pending | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  useEffect(() => {
    registerConfirm(confirm);
    return () => registerConfirm(null);
  }, [confirm]);

  useEffect(() => {
    if (!pending) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('confirm-primary-action')?.focus();
    });
    return () => {
      document.body.style.overflow = previous;
      window.cancelAnimationFrame(frame);
    };
  }, [pending]);

  const close = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  const isDanger = pending?.variant === 'danger';

  return (
    <>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="absolute inset-0 bg-gray-950/40 backdrop-blur-[2px]" />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={pending.description ? descriptionId : undefined}
            className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-6"
          >
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 tracking-tight">
              {pending.title}
            </h2>
            {pending.description && (
              <p id={descriptionId} className="mt-2 text-sm leading-relaxed text-gray-600">
                {pending.description}
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => close(false)}>
                {pending.cancelLabel || 'Keep editing'}
              </Button>
              <Button
                type="button"
                className={isDanger ? '!bg-red-600 hover:!bg-red-700 focus:!ring-red-500' : undefined}
                id="confirm-primary-action"
                onClick={() => close(true)}
              >
                {pending.confirmLabel || 'Continue'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfirmProvider;
